import React, { useState, useEffect, useRef } from 'react';
import { TAB_ID } from 'src/_shared/broadcast-tab-id.js';
import {
	EditOutlined,
	SaveOutlined,
	SettingOutlined,
	CloseOutlined,
	UpOutlined,
	DownOutlined,
	PlusOutlined,
	DeleteOutlined,
	DownloadOutlined,
	UploadOutlined,
} from '@ant-design/icons';
import { Collapse, Select, Input, Button, Modal, message, Tooltip } from 'antd';
import { withApollo } from 'react-apollo';
import { gql, useApolloClient } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { Link, withRouter } from 'react-router-dom';
import WebIcon from 'src/_shared/components/WebIconComponent.jsx';
import { USER_ROLES } from 'src/_shared/constants';
import {
	DEFAULT_MENUS,
	AVAILABLE_ICONS,
} from 'src/customizable-ui/aside/defaults.js';
import { queryCustomPageByCompanyIdIndex } from 'src/_shared/api/graphql/custom/custom-page/queryCustomPageGraphql.js';
import { updateCompany } from 'src/_shared/api/graphql/custom/company/updateCompanyGraphql.js';
import { COLORS } from 'src/_shared/styles/colors';
import { downloadFromS3, parse } from 'src/_shared/services/utils.js';
import Logo from 'src/_shared/assets/erinwhite.png';
import { LinkEditorModal } from 'src/customizable-ui/aside/LinkEditorModal.jsx';
import { withQueryParams } from 'src/_shared/services/index.js';
import { IconSelector } from 'src/customizable-ui/aside/IconSelectorComponent.jsx';
import 'src/customizable-ui/aside/CustomizableAside.scss';
import { uploadToS3 } from 'src/_shared/services/utils.js';
import { usePermission } from 'src/_shared/components/permission-gate/usePermission.js';
import { hasPermission } from 'src/_shared/components/permission-gate/pathHelpers.js';
import { isPhone, onPhoneChange } from 'src/_shared/phone.js';
import { getHeaderNavItems } from 'src/_shared/navigation/header/headerNavItems.js';

const BaseCustomizableAside = ({
	currentUser,
	show,
	updateDisplay,
	setCurrentUser,
}) => {
	const customNav = currentUser?.company?.customNav
		? JSON.parse(currentUser.company.customNav)
		: null;
	const [isEditMode, setIsEditMode] = useState(false);
	const [editingItem, setEditingItem] = useState(null);
	const [editingPanel, setEditingPanel] = useState(null);
	const [customizations, setCustomizations] = useState(() => {
		if (customNav) {
			const customNavUpdated = {
				...customNav,
				[USER_ROLES.SUPER_ADMIN]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.SUPER_ADMIN])
				),
			};
			return customNavUpdated;
		} else {
			return {
				[USER_ROLES.EMPLOYEE]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.EMPLOYEE])
				),
				[USER_ROLES.MANAGER]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.MANAGER])
				),
				[USER_ROLES.ADMIN]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.ADMIN])
				),
				[USER_ROLES.SUPER_ADMIN]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.SUPER_ADMIN])
				),
			};
		}
	});
	const [tempCustomizations, setTempCustomizations] = useState(null);
	const [iconSelectorVisible, setIconSelectorVisible] = useState(false);
	const [iconSelectorTarget, setIconSelectorTarget] = useState(null);
	const [linkEditorVisible, setLinkEditorVisible] = useState(false);
	const [linkEditorTarget, setLinkEditorTarget] = useState(null);
	const [linkEditorData, setLinkEditorData] = useState(null);
	const [customPageRoutes, setCustomPageRoutes] = useState([]);
	const [customPages, setCustomPages] = useState([]);
	// On phones the header's horizontal nav is hidden, so its pages (Jobs,
	// Announcements, custom pages) are listed here as well (ERIN-8561).
	const [phone, setPhone] = useState(isPhone);
	useEffect(() => onPhoneChange(setPhone), []);

	useEffect(() => {
		const newCustomNav = currentUser?.company?.customNav
			? JSON.parse(currentUser.company.customNav)
			: null;
		if (newCustomNav) {
			setCustomizations({
				...newCustomNav,
				[USER_ROLES.SUPER_ADMIN]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.SUPER_ADMIN])
				),
			});
		} else {
			setCustomizations({
				[USER_ROLES.EMPLOYEE]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.EMPLOYEE])
				),
				[USER_ROLES.MANAGER]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.MANAGER])
				),
				[USER_ROLES.ADMIN]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.ADMIN])
				),
				[USER_ROLES.SUPER_ADMIN]: JSON.parse(
					JSON.stringify(DEFAULT_MENUS[USER_ROLES.SUPER_ADMIN])
				),
			});
		}
	}, [currentUser?.company?.customNav]);

	const apolloClient = useApolloClient();
	const allowEdit = usePermission('action.nav.edit');
	const showViewAsSelector = usePermission('ui.nav.view-as-selector');

	useEffect(() => {
		const companyId = currentUser?.companyId || currentUser?.company?.id;
		if (!companyId || !apolloClient) return;

		apolloClient
			.query({
				query: queryCustomPageByCompanyIdIndex,
				variables: { companyId, first: 100 },
				fetchPolicy: 'network-only',
			})
			.then(({ data }) => {
				const pages = data?.queryCustomPageByCompanyIdIndex?.items || [];
				setCustomPages(pages);
				const activePageRoutes = pages
					.filter((page) => page.active)
					.map((page) => ({
						value: `/custompage/${page.pageNumber}`,
						label: `Custom Page: ${page.title}`,
					}));
				setCustomPageRoutes(activePageRoutes);
			})
			.catch((err) => {
				console.error('Failed to fetch custom pages for sidebar:', err);
			});
	}, [currentUser?.companyId, currentUser?.company?.id]);

	const filterMenuItemsByScopedPolicy = (menuItems, scopedPolicy) => {
		const allowedPaths = scopedPolicy?.paths || [];
		if (allowedPaths.length === 0) return menuItems;

		return menuItems
			.map((item) => {
				if (!item || Object.keys(item).length === 0) return null;

				const itemLink = item.link;
				const hasItemPermission = itemLink
					? hasPermission(itemLink, allowedPaths)
					: false;

				if (item.panels && Array.isArray(item.panels)) {
					const filteredPanels = item.panels.filter((panel) => {
						if (!panel || Object.keys(panel).length === 0) return false; // Changed from true to false
						const panelLink = panel.link;
						return panelLink ? hasPermission(panelLink, allowedPaths) : false; // Changed from true to false
					});

					if (filteredPanels.length === 0) return null;

					return { ...item, panels: filteredPanels };
				}

				return hasItemPermission ? item : null;
			})
			.filter((item) => item !== null);
	};

	const getDisplayRole = (userRole) => {
		if (
			userRole === USER_ROLES.EMPLOYEE ||
			userRole === USER_ROLES.EXTENDED_USER
		) {
			return USER_ROLES.EMPLOYEE;
		}
		return userRole;
	};

	const [selectedRole, setSelectedRole] = useState(() => {
		return getDisplayRole(currentUser?.role || USER_ROLES.EMPLOYEE);
	});
	const [expandedPanels, setExpandedPanels] = useState([]);
	const [logoUrl, setLogoUrl] = useState('');

	// Custom Report nav links are only offered to super admins editing the Admin
	// nav, matching where legacy custom reports already appear.
	const allowCustomReport =
		currentUser?.role === USER_ROLES.SUPER_ADMIN &&
		selectedRole === USER_ROLES.ADMIN;

	const fileInputRef = useRef(null);
	const saveCustomizations = async (
		customizationsToSave,
		metabaseReports = null
	) => {
		try {
			const publishData = customizationsToSave;
			const jsonBlob = new Blob([JSON.stringify(publishData, null, 2)], {
				type: 'application/json',
			});
			await uploadToS3(
				jsonBlob,
				`nav/${currentUser.companyId}/nav-layout.json`,
				'customizable-ui'
			);
			setCurrentUser({
				...currentUser,
				company: {
					...currentUser.company,
					customNav: JSON.stringify(publishData),
					...(metabaseReports ? { metabaseReports } : {}),
				},
			});
			message.success('Navigation changes published successfully');
		} catch (error) {
			console.error('Error publishing navigation changes:', error);
			message.error('Failed to publish navigation changes');
		}
	};

	const reportSlug = (label = '') =>
		`${label.toLowerCase()?.replaceAll(' ', '-')}`;

	// Upserts any "Custom Report" nav panels into company.metabaseReports so the
	// /custom-report/:slug route + renderer (driven by metabaseReports) resolve.
	// Matches existing reports by slug and never deletes; returns the merged list
	// when a write happened, otherwise null.
	const persistCustomReports = async (customizationsToSave) => {
		try {
			const navReports = [];
			Object.values(customizationsToSave || {}).forEach((items) => {
				(items || []).forEach((item) => {
					(item?.panels || []).forEach((panel) => {
						if (
							panel?.linkType === 'customReport' &&
							panel?.title &&
							panel?.reportId &&
							panel?.reportType
						) {
							navReports.push(panel);
						}
					});
				});
			});

			if (!navReports.length) return null;

			const existing = currentUser?.company?.metabaseReports || [];
			const merged = existing.map((report) => ({
				id: report.id,
				label: report.label,
				reportId: report.reportId,
				reportType: report.reportType,
			}));
			let changed = false;

			navReports.forEach((panel) => {
				const slug = reportSlug(panel.title);
				const index = merged.findIndex((r) => reportSlug(r.label) === slug);
				const next = {
					label: panel.title,
					reportId: panel.reportId,
					reportType: panel.reportType,
				};
				if (index >= 0) {
					const current = merged[index];
					if (
						current.label !== next.label ||
						current.reportId !== next.reportId ||
						current.reportType !== next.reportType
					) {
						merged[index] = { ...current, ...next };
						changed = true;
					}
				} else {
					merged.push({ id: uuidv4(), ...next });
					changed = true;
				}
			});

			if (!changed) return null;

			await apolloClient.mutate({
				mutation: gql(updateCompany),
				variables: {
					input: {
						id: currentUser.companyId,
						metabaseReports: merged,
					},
				},
			});

			return merged;
		} catch (error) {
			console.error('Error saving custom reports:', error);
			message.error('Failed to save custom report');
			return null;
		}
	};

	const getCustomizedValue = (
		itemIndex,
		panelIndex = null,
		field,
		defaultValue
	) => {
		const source = isEditMode ? tempCustomizations : customizations;
		const menuItems = source?.[selectedRole] || [];
		const item = menuItems[itemIndex];

		if (!item) return defaultValue;

		if (panelIndex !== null) {
			const panel = item.panels?.[panelIndex];
			return panel?.[field] !== undefined ? panel[field] : defaultValue;
		} else {
			return item[field] !== undefined ? item[field] : defaultValue;
		}
	};

	const updateCustomization = (itemIndex, panelIndex = null, field, value) => {
		const updatedCustomizations = { ...tempCustomizations };
		const menuItems = [...(updatedCustomizations[selectedRole] || [])];

		if (!menuItems[itemIndex]) return;

		if (panelIndex !== null) {
			if (!menuItems[itemIndex].panels) {
				menuItems[itemIndex] = { ...menuItems[itemIndex], panels: [] };
			}
			const panels = [...menuItems[itemIndex].panels];
			panels[panelIndex] = {
				...panels[panelIndex],
				[field]: value,
			};
			menuItems[itemIndex] = {
				...menuItems[itemIndex],
				panels,
			};
		} else {
			menuItems[itemIndex] = {
				...menuItems[itemIndex],
				[field]: value,
			};
		}

		updatedCustomizations[selectedRole] = menuItems;
		setTempCustomizations(updatedCustomizations);
	};

	const getMenuItems = () => {
		const source = isEditMode ? tempCustomizations : customizations;
		let menuItems = source?.[selectedRole] || [];

		if (currentUser?.isScopedUser && !isEditMode) {
			let scopedPolicy = currentUser?.scopedPolicy || {};
			if (typeof scopedPolicy === 'string') {
				try {
					scopedPolicy = JSON.parse(scopedPolicy);
				} catch (e) {
					scopedPolicy = {};
					console.error(e);
				}
			}
			const allowedPaths = scopedPolicy?.paths || [];

			if (allowedPaths.length > 0) {
				menuItems = filterMenuItemsByScopedPolicy(menuItems, scopedPolicy);
			}
		}

		return menuItems;
	};

	const getAvailableRolesForCustomization = () => {
		const userRole = currentUser?.role;
		if (userRole === USER_ROLES.SUPER_ADMIN) {
			return [USER_ROLES.EMPLOYEE, USER_ROLES.MANAGER, USER_ROLES.ADMIN];
		} else if (userRole === USER_ROLES.ADMIN) {
			return [USER_ROLES.EMPLOYEE, USER_ROLES.MANAGER];
		}
		return [];
	};

	const getAvailableRolesForViewing = () => {
		const userRole = currentUser?.role;
		if (userRole === USER_ROLES.SUPER_ADMIN) {
			return [
				USER_ROLES.EMPLOYEE,
				USER_ROLES.MANAGER,
				USER_ROLES.ADMIN,
				USER_ROLES.SUPER_ADMIN,
			];
		} else if (userRole === USER_ROLES.ADMIN) {
			return [USER_ROLES.EMPLOYEE, USER_ROLES.MANAGER, USER_ROLES.ADMIN];
		}
		return [];
	};

	const toggleEditMode = () => {
		if (isEditMode) {
			Modal.confirm({
				title: 'Exit Edit Mode',
				content: 'Do you want to save your changes?',
				okText: 'Save',
				cancelText: 'Discard',
				onOk: () => {
					handleSaveCustomizations();
				},
				onCancel: () => {
					setIsEditMode(false);
					setTempCustomizations(null);
					setEditingItem(null);
					setEditingPanel(null);
					setExpandedPanels([]);
					setSelectedRole(getDisplayRole(currentUser?.role));
				},
			});
		} else {
			const availableRoles = getAvailableRolesForCustomization();
			const validRole = availableRoles.includes(selectedRole)
				? selectedRole
				: availableRoles[0] || USER_ROLES.EMPLOYEE;

			setSelectedRole(validRole);

			const menuItems = customizations[validRole] || [];
			const expandedKeys = menuItems
				.map((item, index) => {
					const hasPanels = item.panels && item.panels.length > 0;
					return hasPanels ? `panel-${index}` : null;
				})
				.filter(Boolean);

			setIsEditMode(true);
			setTempCustomizations({ ...customizations });
			setExpandedPanels(expandedKeys);
		}
	};

	const handleSaveCustomizations = async () => {
		const mergedReports = await persistCustomReports(tempCustomizations);
		await saveCustomizations(tempCustomizations, mergedReports);
		setCustomizations(tempCustomizations);
		setIsEditMode(false);
		setTempCustomizations(null);
		setEditingItem(null);
		setEditingPanel(null);
		setExpandedPanels([]);
		setSelectedRole(getDisplayRole(currentUser?.role));
	};

	const handleCancelEdit = () => {
		setIsEditMode(false);
		setTempCustomizations(null);
		setEditingItem(null);
		setEditingPanel(null);
		setExpandedPanels([]);
		setSelectedRole(getDisplayRole(currentUser?.role));
	};

	const handleIconSelect = (iconName) => {
		if (iconSelectorTarget) {
			const { itemIndex, panelIndex } = iconSelectorTarget;
			updateCustomization(itemIndex, panelIndex, 'icon', iconName);
		}
		setIconSelectorVisible(false);
		setIconSelectorTarget(null);
	};

	const showIconSelector = (itemIndex, panelIndex = null) => {
		setIconSelectorVisible(true);
		setIconSelectorTarget({ itemIndex, panelIndex });
	};

	const showLinkEditor = (itemIndex, panelIndex = null) => {
		const item = getMenuItems()[itemIndex];
		const currentLink = getCustomizedValue(
			itemIndex,
			panelIndex,
			'link',
			item.link
		);
		const currentPanels =
			panelIndex === null
				? getCustomizedValue(itemIndex, null, 'panels', item.panels || [])
				: [];

		setLinkEditorVisible(true);
		setLinkEditorTarget({ itemIndex, panelIndex });
		setLinkEditorData({
			link: currentLink,
			panels: currentPanels,
		});
	};

	const handleLinkEditorSave = (data) => {
		if (linkEditorTarget) {
			const { itemIndex, panelIndex } = linkEditorTarget;

			if (panelIndex !== null) {
				updateCustomization(itemIndex, panelIndex, 'link', data.link);
			} else {
				updateCustomization(itemIndex, null, 'link', data.link);

				if (data.panels && data.panels.length > 0) {
					updateCustomization(itemIndex, null, 'panels', data.panels);
				}
			}
		}

		setLinkEditorVisible(false);
		setLinkEditorTarget(null);
		setLinkEditorData(null);
	};

	const handleAddMenuItem = () => {
		const newItem = {
			title: 'New Menu Item',
			iconType: 'menu',
			link: '/new-link',
		};

		const updatedCustomizations = { ...tempCustomizations };
		const menuItems = [...(updatedCustomizations[selectedRole] || [])];
		menuItems.push(newItem);
		updatedCustomizations[selectedRole] = menuItems;

		setTempCustomizations(updatedCustomizations);
		message.success('New menu item added');
	};

	const handleRemoveMenuItem = (itemIndex) => {
		Modal.confirm({
			title: 'Remove Menu Item',
			content: 'Are you sure you want to remove this menu item?',
			okText: 'Remove',
			okType: 'danger',
			onOk: () => {
				const updatedCustomizations = { ...tempCustomizations };
				const menuItems = [...(updatedCustomizations[selectedRole] || [])];
				menuItems.splice(itemIndex, 1);
				updatedCustomizations[selectedRole] = menuItems;

				setTempCustomizations(updatedCustomizations);
				message.success('Menu item removed');
			},
		});
	};

	const moveItemUp = (index) => {
		if (index === 0) return;

		const updatedCustomizations = { ...tempCustomizations };
		const menuItems = [...(updatedCustomizations[selectedRole] || [])];
		[menuItems[index - 1], menuItems[index]] = [
			menuItems[index],
			menuItems[index - 1],
		];
		updatedCustomizations[selectedRole] = menuItems;

		setTempCustomizations(updatedCustomizations);
	};

	const moveItemDown = (index) => {
		const menuItems = tempCustomizations[selectedRole] || [];
		if (index === menuItems.length - 1) return;

		const updatedCustomizations = { ...tempCustomizations };
		const updatedMenuItems = [...menuItems];
		[updatedMenuItems[index], updatedMenuItems[index + 1]] = [
			updatedMenuItems[index + 1],
			updatedMenuItems[index],
		];
		updatedCustomizations[selectedRole] = updatedMenuItems;

		setTempCustomizations(updatedCustomizations);
	};

	const handleExportCustomizations = () => {
		try {
			const customizationsToExport = isEditMode
				? tempCustomizations
				: customizations;
			const dataStr = JSON.stringify(customizationsToExport, null, 2);
			const dataBlob = new Blob([dataStr], { type: 'application/json' });
			const url = URL.createObjectURL(dataBlob);
			const link = document.createElement('a');
			link.href = url;
			link.download = `aside-customizations-${new Date().toISOString()}.json`;
			document.body.appendChild(link);
			link.click();
			document.body.removeChild(link);
			URL.revokeObjectURL(url);
			message.success('Customizations exported successfully');
		} catch (error) {
			console.error('Error exporting customizations:', error);
			message.error('Failed to export customizations');
		}
	};

	const handleImportCustomizations = (event) => {
		const file = event.target.files?.[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = (e) => {
			try {
				const importedData = JSON.parse(e.target.result);

				if (isEditMode) {
					setTempCustomizations(importedData);
					message.success(
						'Customizations imported. Click Save to apply changes.'
					);
				} else {
					setCustomizations(importedData);
					message.success('Customizations imported and applied successfully');
				}
			} catch (error) {
				console.error('Error importing customizations:', error);
				message.error('Failed to import customizations. Invalid JSON file.');
			}
		};
		reader.onerror = () => {
			message.error('Failed to read file');
		};
		reader.readAsText(file);

		if (fileInputRef.current) {
			fileInputRef.current.value = '';
		}
	};

	const handleImportClick = () => {
		fileInputRef.current?.click();
	};

	const handleLinkClick = (e, itemIndex, panelIndex = null) => {
		if (isEditMode) {
			e.preventDefault();
			e.stopPropagation();
			return false;
		}
	};

	const renderEditableText = (
		text,
		itemIndex,
		panelIndex = null,
		field = 'title'
	) => {
		const value = getCustomizedValue(itemIndex, panelIndex, field, text);
		const isEditing =
			isEditMode && editingItem === itemIndex && editingPanel === panelIndex;

		if (!isEditMode) {
			return <span className="menu-title">{value}</span>;
		}

		return (
			<div className="editable-text-wrapper">
				{isEditing ? (
					<Input
						value={value}
						onChange={(e) =>
							updateCustomization(itemIndex, panelIndex, field, e.target.value)
						}
						onPressEnter={() => {
							setEditingItem(null);
							setEditingPanel(null);
						}}
						onBlur={() => {
							setEditingItem(null);
							setEditingPanel(null);
						}}
						autoFocus
						size="small"
						className="edit-input"
					/>
				) : (
					<span
						className="menu-title editable"
						onClick={() => {
							setEditingItem(itemIndex);
							setEditingPanel(panelIndex);
						}}
					>
						{value}
						<EditOutlined className="edit-icon" />
					</span>
				)}
			</div>
		);
	};

	const renderEditableIcon = (iconType, itemIndex, panelIndex = null) => {
		const customIcon = getCustomizedValue(
			itemIndex,
			panelIndex,
			'icon',
			iconType
		);

		const isCustomIcon = typeof customIcon === 'object' && customIcon?.dataUrl;

		return (
			<span className="menu-icon">
				{isCustomIcon ? (
					<img
						src={customIcon.dataUrl}
						alt={customIcon.name || 'Custom icon'}
						style={{
							width: '30px',
							height: '30px',
							objectFit: 'contain',
						}}
					/>
				) : (
					<WebIcon name={customIcon} size={30} color={COLORS.white} />
				)}
				{isEditMode && (
					<Tooltip title="Change Icon">
						<EditOutlined
							className="icon-edit-button"
							onClick={(e) => {
								e.stopPropagation();
								showIconSelector(itemIndex, panelIndex);
							}}
						/>
					</Tooltip>
				)}
			</span>
		);
	};

	const renderEditControls = () => {
		if (
			(currentUser?.role !== USER_ROLES.ADMIN &&
				currentUser?.role !== USER_ROLES.SUPER_ADMIN) ||
			(currentUser?.isScopedUser && !allowEdit)
		) {
			return null;
		}

		return (
			<div className="edit-controls">
				{!isEditMode ? (
					<>
						<Button
							type="primary"
							icon={<EditOutlined />}
							onClick={toggleEditMode}
							className="edit-mode-button"
						>
							Customize Navigation
						</Button>
						<div className="import-export-buttons">
							<Tooltip title="Export Customizations">
								<Button
									icon={<DownloadOutlined />}
									onClick={handleExportCustomizations}
									size="small"
								>
									Export
								</Button>
							</Tooltip>
							<Tooltip title="Import Customizations">
								<Button
									icon={<UploadOutlined />}
									onClick={handleImportClick}
									size="small"
								>
									Import
								</Button>
							</Tooltip>
							<input
								ref={fileInputRef}
								type="file"
								accept=".json"
								onChange={handleImportCustomizations}
								style={{ display: 'none' }}
							/>
						</div>
					</>
				) : (
					<div className="edit-action-buttons">
						<Button
							type="primary"
							icon={<SaveOutlined />}
							onClick={handleSaveCustomizations}
							className="save-button"
						>
							Save
						</Button>
						<Button
							icon={<CloseOutlined />}
							onClick={handleCancelEdit}
							className="cancel-button"
						>
							Cancel
						</Button>
						<div className="import-export-buttons">
							<Tooltip title="Export Customizations">
								<Button
									icon={<DownloadOutlined />}
									onClick={handleExportCustomizations}
									size="small"
								>
									Export
								</Button>
							</Tooltip>
							<Tooltip title="Import Customizations">
								<Button
									icon={<UploadOutlined />}
									onClick={handleImportClick}
									size="small"
								>
									Import
								</Button>
							</Tooltip>
							<input
								ref={fileInputRef}
								type="file"
								accept=".json"
								onChange={handleImportCustomizations}
								style={{ display: 'none' }}
							/>
						</div>
					</div>
				)}
			</div>
		);
	};

	const renderRoleSelector = () => {
		if (!isEditMode || (currentUser?.isScopedUser && !allowEdit)) return null;

		const availableRoles = getAvailableRolesForCustomization();

		return (
			<div className="role-selector-wrapper">
				<label>Customize for Role:</label>
				<Select
					value={selectedRole}
					onChange={(value) => setSelectedRole(value)}
					style={{ width: '100%' }}
				>
					{availableRoles.includes(USER_ROLES.EMPLOYEE) && (
						<Select.Option value={USER_ROLES.EMPLOYEE}>Employee</Select.Option>
					)}
					{availableRoles.includes(USER_ROLES.MANAGER) && (
						<Select.Option value={USER_ROLES.MANAGER}>Manager</Select.Option>
					)}
					{availableRoles.includes(USER_ROLES.ADMIN) && (
						<Select.Option value={USER_ROLES.ADMIN}>Admin</Select.Option>
					)}
				</Select>
			</div>
		);
	};

	const handleViewAsChange = (value) => {
		setSelectedRole(value);
		if (updateDisplay) {
			updateDisplay(value);
			if (currentUser.role === value) {
				localStorage.setItem('userDisplayAs', '');
			} else {
				localStorage.setItem('userDisplayAs', value);
			}
		}
	};

	const renderViewAsSelector = () => {
		const userRole = currentUser?.role;

		if (
			isEditMode ||
			userRole === USER_ROLES.EMPLOYEE ||
			userRole === USER_ROLES.EXTENDED_USER ||
			userRole === USER_ROLES.MANAGER
		) {
			return null;
		}

		if (
			(userRole !== USER_ROLES.ADMIN && userRole !== USER_ROLES.SUPER_ADMIN) ||
			!showViewAsSelector
		) {
			return null;
		}

		const availableRoles = getAvailableRolesForViewing();

		return (
			<div className="role-selector-wrapper view-as-selector">
				<label>View As:</label>
				<Select
					value={selectedRole}
					onChange={handleViewAsChange}
					style={{ width: '100%' }}
					size="small"
				>
					{availableRoles.includes(USER_ROLES.EMPLOYEE) && (
						<Select.Option value={USER_ROLES.EMPLOYEE}>Employee</Select.Option>
					)}
					{availableRoles.includes(USER_ROLES.MANAGER) && (
						<Select.Option value={USER_ROLES.MANAGER}>Manager</Select.Option>
					)}
					{availableRoles.includes(USER_ROLES.ADMIN) && (
						<Select.Option value={USER_ROLES.ADMIN}>Admin</Select.Option>
					)}
					{availableRoles.includes(USER_ROLES.SUPER_ADMIN) && (
						<Select.Option value={USER_ROLES.SUPER_ADMIN}>
							Super Admin
						</Select.Option>
					)}
				</Select>
			</div>
		);
	};

	const renderMenuItem = (item, itemIndex) => {
		const hasPanels = item.panels && item.panels.length > 0;

		if (hasPanels) {
			return renderCollapsibleItem(item, itemIndex);
		}
		return renderSimpleItem(item, itemIndex);
	};

	const renderSimpleItem = (item, itemIndex) => {
		const customLink = isEditMode
			? getCustomizedValue(itemIndex, null, 'link', item.link)
			: item.link || '';
		const customIcon = isEditMode
			? getCustomizedValue(itemIndex, null, 'icon', item.iconType)
			: item.icon || item.iconType;
		const customTitle = isEditMode
			? getCustomizedValue(itemIndex, null, 'title', item.title)
			: item.title || '';

		const isExternal =
			customLink &&
			(customLink.startsWith('http://') || customLink.startsWith('https://'));

		const menuItems = getMenuItems();
		const isFirst = itemIndex === 0;
		const isLast = itemIndex === menuItems.length - 1;

		const isCustomIcon = typeof customIcon === 'object' && customIcon?.dataUrl;

		const linkContent = (
			<>
				{isEditMode && (
					<div className="reorder-controls">
						<Button
							type="text"
							size="small"
							icon={<UpOutlined color="white" />}
							disabled={isFirst}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								moveItemUp(itemIndex);
							}}
							className="move-up-btn"
						/>
						<Button
							type="text"
							size="small"
							icon={<DownOutlined />}
							disabled={isLast}
							onClick={(e) => {
								e.preventDefault();
								e.stopPropagation();
								moveItemDown(itemIndex);
							}}
							className="move-down-btn"
						/>
					</div>
				)}
				<span className="menu-icon">
					{isCustomIcon ? (
						<img
							src={customIcon.dataUrl}
							alt={customIcon.name || 'Custom icon'}
							style={{
								width: '30px',
								height: '30px',
								objectFit: 'contain',
							}}
						/>
					) : (
						<WebIcon name={customIcon} size={30} color={COLORS.white} />
					)}
					{isEditMode && (
						<Tooltip title="Change Icon">
							<EditOutlined
								className="icon-edit-button"
								onClick={(e) => {
									e.stopPropagation();
									showIconSelector(itemIndex);
								}}
							/>
						</Tooltip>
					)}
				</span>
				{isEditMode ? (
					renderEditableText(customTitle, itemIndex, null, 'title')
				) : (
					<span className="menu-title">{customTitle}</span>
				)}
				{isEditMode && (
					<>
						<Tooltip title="Edit Link">
							<SettingOutlined
								className="link-edit-button"
								style={{ marginRight: 4 }}
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									showLinkEditor(itemIndex);
								}}
							/>
						</Tooltip>
						<Tooltip title="Remove Item">
							<DeleteOutlined
								className="delete-button"
								onClick={(e) => {
									e.preventDefault();
									e.stopPropagation();
									handleRemoveMenuItem(itemIndex);
								}}
							/>
						</Tooltip>
					</>
				)}
			</>
		);

		return (
			<li key={itemIndex} className="menu-item">
				{isEditMode ? (
					<div className="menu-link">{linkContent}</div>
				) : isExternal ? (
					<a
						href={customLink}
						className="menu-link"
						target="_blank"
						rel="noopener noreferrer"
					>
						{linkContent}
					</a>
				) : (
					<Link
						to={customLink}
						className={`menu-link ${
							customLink === window.location.pathname ? 'active' : 'inactive'
						}`}
					>
						{linkContent}
					</Link>
				)}
			</li>
		);
	};

	const renderCollapsibleItem = (item, itemIndex) => {
		const panelsToRender = item.panels || [];
		const panelKey = `panel-${itemIndex}`;

		const menuItems = getMenuItems();
		const isFirst = itemIndex === 0;
		const isLast = itemIndex === menuItems.length - 1;
		let matchingChild = false;
		panelsToRender.forEach((panel) => {
			if (panel.link === window.location.pathname) {
				matchingChild = true;
			}
		});

		const customIcon = isEditMode
			? getCustomizedValue(itemIndex, null, 'icon', item.iconType)
			: item.icon || item.iconType;
		const customTitle = isEditMode
			? getCustomizedValue(itemIndex, null, 'title', item.title)
			: item.title || '';

		const isCustomIcon = typeof customIcon === 'object' && customIcon?.dataUrl;

		const collapseProps = isEditMode
			? {
					activeKey: expandedPanels,
				}
			: matchingChild
				? {
						defaultActiveKey: [panelKey],
					}
				: {};

		return (
			<li key={itemIndex} className="menu-item">
				<Collapse
					expandIconPosition="end"
					bordered={false}
					{...collapseProps}
					expandIcon={isEditMode ? () => null : undefined}
				>
					<Collapse.Panel
						key={panelKey}
						showArrow={!isEditMode}
						header={
							<div className="menu-link">
								{isEditMode && (
									<div className="reorder-controls">
										<Button
											type="text"
											size="small"
											icon={<UpOutlined />}
											disabled={isFirst}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												moveItemUp(itemIndex);
											}}
											className="move-up-btn"
										/>
										<Button
											type="text"
											size="small"
											icon={<DownOutlined />}
											disabled={isLast}
											onClick={(e) => {
												e.preventDefault();
												e.stopPropagation();
												moveItemDown(itemIndex);
											}}
											className="move-down-btn"
										/>
									</div>
								)}
								<span className="menu-icon">
									{isCustomIcon ? (
										<img
											src={customIcon.dataUrl}
											alt={customIcon.name || 'Custom icon'}
											style={{
												width: '30px',
												height: '30px',
												objectFit: 'contain',
											}}
										/>
									) : (
										<WebIcon name={customIcon} size={30} color={COLORS.white} />
									)}
									{isEditMode && (
										<Tooltip title="Change Icon">
											<EditOutlined
												className="icon-edit-button"
												onClick={(e) => {
													e.stopPropagation();
													showIconSelector(itemIndex);
												}}
											/>
										</Tooltip>
									)}
								</span>
								{isEditMode ? (
									renderEditableText(customTitle, itemIndex, null, 'title')
								) : (
									<span className="menu-title">{customTitle}</span>
								)}
								{isEditMode && (
									<>
										<Tooltip title="Edit Navigation Item">
											<SettingOutlined
												className="link-edit-button"
												style={{ marginRight: 4 }}
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													showLinkEditor(itemIndex);
												}}
											/>
										</Tooltip>
										<Tooltip title="Remove Item">
											<DeleteOutlined
												className="delete-button"
												onClick={(e) => {
													e.preventDefault();
													e.stopPropagation();
													handleRemoveMenuItem(itemIndex);
												}}
											/>
										</Tooltip>
									</>
								)}
							</div>
						}
					>
						<ul className="sub-menu">
							{panelsToRender.map((panel, panelIndex) => {
								const customLink = isEditMode
									? getCustomizedValue(
											itemIndex,
											panelIndex,
											'link',
											panel.link
										)
									: panel.link || '';
								const customTitle = isEditMode
									? getCustomizedValue(
											itemIndex,
											panelIndex,
											'title',
											panel.title
										)
									: panel.title || '';

								const isExternal =
									customLink &&
									(customLink.startsWith('http://') ||
										customLink.startsWith('https://'));

								const linkContent = isEditMode ? (
									<>
										{renderEditableText(
											customTitle,
											itemIndex,
											panelIndex,
											'title'
										)}
									</>
								) : (
									<span className="menu-title">{customTitle}</span>
								);

								return (
									<li key={panelIndex} className="sub-menu-item custom-nav">
										{isExternal ? (
											<a
												href={customLink}
												className="sub-menu-link"
												target="_blank"
												rel="noopener noreferrer"
												onClick={(e) =>
													handleLinkClick(e, itemIndex, panelIndex)
												}
											>
												{linkContent}
											</a>
										) : (
											<Link
												to={customLink}
												className={`sub-menu-link ${
													customLink === window.location.pathname
														? 'active'
														: 'inactive'
												}`}
												style={{ width: '100%' }}
												onClick={(e) =>
													handleLinkClick(e, itemIndex, panelIndex)
												}
											>
												{linkContent}
											</Link>
										)}
									</li>
								);
							})}
						</ul>
					</Collapse.Panel>
				</Collapse>
			</li>
		);
	};

	useEffect(() => {
		const fetchCompanyLogo = async () => {
			try {
				const s3Key =
					currentUser?.subCompany?.logo?.key || currentUser?.company?.logo?.key;
				if (s3Key) {
					const url = await downloadFromS3(s3Key);
					setLogoUrl(url);
				}
			} catch (err) {
				console.log('Error fetching company logo:', err);
			}
		};

		fetchCompanyLogo();
	}, [
		currentUser?.companyId,
		currentUser?.subCompany?.logo?.key,
		currentUser?.company?.logo?.key,
	]);

	useEffect(() => {
		if (!isEditMode && currentUser?.role) {
			setSelectedRole(currentUser.role);
		}
	}, [currentUser?.role, isEditMode]);

	useEffect(() => {
		const channel = new BroadcastChannel('erin-elevated-sync');
		channel.onmessage = (event) => {
			if (event.data?.type === 'USER_CHANGED' && event.data?.tabId !== TAB_ID) {
				window.location.reload();
			}
		};

		return () => channel.close();
	}, []);

	if (!currentUser?.company) return null;

	const menuItems = getMenuItems();

	return (
		<aside
			className={`sidebar customizable-aside ${show ? 'sidebar-close' : ''} ${isEditMode ? 'edit-mode' : ''}`}
			id="customizable-aside"
		>
			{logoUrl ? (
				<div className="sidebar-logo">
					<img src={logoUrl} alt="logo" />
				</div>
			) : (
				<div className="sidebar-logo">
					<img src={Logo} alt="logo" />
				</div>
			)}

			{renderEditControls()}
			{renderRoleSelector()}
			{renderViewAsSelector()}

			{isEditMode && (
				<div className="add-menu-item-wrapper">
					<Button
						type="dashed"
						icon={<PlusOutlined />}
						onClick={handleAddMenuItem}
						block
						className="add-menu-item-btn"
					>
						Add Menu Item
					</Button>
				</div>
			)}

			<ul className="side-menu">
				{menuItems.map((item, itemIndex) => {
					return renderMenuItem(item, itemIndex);
				})}
				{phone &&
					!isEditMode &&
					getHeaderNavItems({ currentUser, customPageData: customPages })
						.filter(
							(extra) => !menuItems.some((item) => item?.link === extra.link)
						)
						.map((extra, index) =>
							renderSimpleItem(
								{
									title: extra.title,
									link: extra.link,
									iconType: extra.iconType,
								},
								menuItems.length + index
							)
						)}
			</ul>

			<IconSelector
				visible={iconSelectorVisible}
				onSelect={handleIconSelect}
				onCancel={() => {
					setIconSelectorVisible(false);
					setIconSelectorTarget(null);
				}}
				availableIcons={AVAILABLE_ICONS}
			/>

			<LinkEditorModal
				visible={linkEditorVisible}
				onSave={handleLinkEditorSave}
				onCancel={() => {
					setLinkEditorVisible(false);
					setLinkEditorTarget(null);
					setLinkEditorData(null);
				}}
				initialData={linkEditorData}
				customPageRoutes={customPageRoutes}
				allowCustomReport={allowCustomReport}
			/>
		</aside>
	);
};

export const CustomizableAsideComponent = withApollo(
	withRouter(withQueryParams(BaseCustomizableAside))
);
